import React, { useState } from "react";
import { Card } from "react-bootstrap";
import { Dash } from "react-bootstrap-icons";
import PropTypes from "prop-types";
import "./StatisticsCard.css";

const StatisticsCard = ({
  title,
  value,
  percentage,
  isPositive,
  subtitle,
  period,
  statistics,
  completedOnly,
  selectedPeriod,
  onViewTrend,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  // Format period_start based on selected period
  const formatPeriodStart = (dateStr, period) => {
    if (!dateStr) return "";

    const [yearStr, monthStr] = dateStr.split("-");
    const year = parseInt(yearStr, 10);
    const month = parseInt(monthStr, 10) - 1; // Convert to 0-based month
    const quarter = Math.floor(month / 3) + 1;

    switch (period) {
      case "monthly": {
        const date = new Date(year, month);
        const monthShort = date.toLocaleDateString("en-US", { month: "short" });
        return `${monthShort} ${year}`;
      }
      case "weekly": {
        const date = new Date(dateStr);
        const monthShort = date.toLocaleDateString("en-US", { month: "short" });
        const day = date.getDate();
        return `${monthShort} ${day}`;
      }
      case "quarterly":
        return `Q${quarter} ${year}`;
      case "yearly":
        return `${year}`;
      default:
        return "";
    }
  };
  const getPeriodLabel = () => {
    switch (period) {
      case "monthly":
        return "MoM";
      case "quarterly":
        return "QoQ";
      case "yearly":
        return "YoY";
      case "weekly":
        return null; // Don't show label for weekly
      default:
        return "MoM";
    }
  };

  const periodLabel = getPeriodLabel();

  // Parse value to separate main value from suffix
  const parseValue = (value) => {
    if (title === "Avg Income" && value.includes("/per job")) {
      const parts = value.split(" /per job");
      return {
        mainValue: parts[0],
        suffix: " /per job",
      };
    }
    return {
      mainValue: value,
      suffix: "",
    };
  };

  const { mainValue, suffix } = parseValue(value);

  // Check if percentage is zero
  const isZeroPercentage = parseFloat(percentage) === 0;

  // Check if value is zero
  const isZeroValue = mainValue === "$0" || mainValue === "0";

  // Event handlers for hover
  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const handleMouseMove = (e) => {
    setTooltipPosition({ x: e.clientX, y: e.clientY });
  };

  // Get data based on title
  const getCurrentValue = () => {
    switch (title) {
      case "Total Income":
        return statistics?.totalIncome?.value || "$0";
      case "Total Jobs":
        return statistics?.totalJobs?.value || "0";
      case "Avg Income":
        return statistics?.avgIncome?.value || "$0";
      default:
        return "0";
    }
  };

  const getPreviousValue = () => {
    switch (title) {
      case "Total Income":
        return statistics?.prevTotalIncome || "$0";
      case "Total Jobs":
        return statistics?.prevTotalJobs || "0";
      case "Avg Income":
        return statistics?.prevAvgIncome || "$0";
      default:
        return "0";
    }
  };

  // Custom Tooltip Component
  const CustomTooltip = () => {
    if (!isHovered || isZeroValue) return null;

    const currentValue = getCurrentValue();
    const previousValue = getPreviousValue();
    const showEstimate = !completedOnly;

    const currentPeriodStart = statistics?.currentPeriodStart;
    const previousPeriodStart = statistics?.previousPeriodStart;

    const formattedCurrentPeriod = formatPeriodStart(
      currentPeriodStart,
      selectedPeriod
    );
    const formattedPreviousPeriod = formatPeriodStart(
      previousPeriodStart,
      selectedPeriod
    );

    return (
      <div
        className="statistics-card-tooltip"
        style={{
          position: "fixed",
          left: `${tooltipPosition.x + 10}px`,
          top: `${tooltipPosition.y + 10}px`,
          zIndex: 9999,
        }}
      >
        <p className="tooltip-value">
          <span className="tooltip-period">
            {formattedCurrentPeriod && `${formattedCurrentPeriod} `}
          </span>
          <span className="tooltip-value-text">
            {showEstimate && <sup className="tooltip-estimate">est.</sup>}
            {currentValue}
            {title === "Avg Income" && " /per job"}
            {title === "Total Jobs" && " jobs"}
          </span>
        </p>
        <p className="tooltip-value">
          <span className="tooltip-period">
            {formattedPreviousPeriod && `${formattedPreviousPeriod} `}
          </span>
          <span className="tooltip-value-text">
            {showEstimate && <sup className="tooltip-estimate">est.</sup>}
            {previousValue}
            {title === "Avg Income" && " /per job"}
            {title === "Total Jobs" && " jobs"}
          </span>
        </p>
      </div>
    );
  };

  return (
    <>
      <Card
        className="statistics-card"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseMove={handleMouseMove}
      >
        <Card.Body className="statistics-card-body">
          <h3 className="statistics-title">{title}</h3>
          {!isZeroValue && (
            <div className="statistics-value">
              <span className="value-main">{mainValue}</span>
              {suffix && <span className="value-suffix">{suffix}</span>}
            </div>
          )}
          {isZeroValue ? (
            <div className="no-data-message">
              <div>No data available</div>
              <div>Add jobs to see results</div>
            </div>
          ) : (
            <div
              className={`statistics-metadata ${isHovered ? "view-trend-button" : ""}`}
              onClick={() => isHovered && onViewTrend && onViewTrend(title)}
            >
              {isHovered ? (
                <span className="view-trend-text">View Trend</span>
              ) : (
                <div className="statistics-change">
                  {isZeroPercentage ? (
                    <div className="percentage-container neutral">
                      <Dash size={32} color="#1C1B1F" />
                    </div>
                  ) : (
                    <div
                      className={`percentage-container ${isPositive ? "positive" : "negative"}`}
                    >
                      <div className="arrow-icon">
                        <svg
                          width="10"
                          height="6"
                          viewBox="0 0 10 6"
                          fill="none"
                        >
                          <path
                            d="M1 5L5 1L9 5"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <span className="percentage-text">{percentage}</span>
                    </div>
                  )}
                  {periodLabel && (
                    <span className="mom-text">{periodLabel}</span>
                  )}
                </div>
              )}
            </div>
          )}
          {subtitle && <div className="statistics-subtitle">{subtitle}</div>}
        </Card.Body>
      </Card>
      <CustomTooltip />
    </>
  );
};

StatisticsCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  percentage: PropTypes.string.isRequired,
  isPositive: PropTypes.bool.isRequired,
  subtitle: PropTypes.string,
  period: PropTypes.oneOf(["monthly", "quarterly", "weekly", "yearly"])
    .isRequired,
  statistics: PropTypes.object,
  completedOnly: PropTypes.bool,
  selectedPeriod: PropTypes.oneOf(["monthly", "quarterly", "weekly", "yearly"]),
  onViewTrend: PropTypes.func,
};

export default StatisticsCard;
